/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { resizeMixin } from '../../utils/resize-mixin.js';
import { windowSize } from '../../utils/window-size.js';

/**
 * @prop {String} position - Defines on which corner of the screen the notifications will be shown. Possible values are `top-right`, `top-left`, `bottom-right` and `bottom-left`
 * @prop {Boolean} mobile - Enables mobile mode. Managed by component automatically.
 * @slot - Takes notification items as children.
 */

export class SHNotifications extends resizeMixin(LitElement) {
  static get properties() {
    return {
      position: { type: String, reflect: true },
      mobile: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          height: fit-content;
          height: -ms-fit-content;
          height: -moz-fit-content;
          width: 20rem;
          display: flex;
          flex-direction: column;
          position: absolute;
          z-index: 5;
        }

        /* positions */
        :host,
        :host([position='top-right']) {
          top: 1rem;
          bottom: auto;
          left: auto;
          right: 1rem;
        }
        :host([position='top-left']) {
          top: 1rem;
          bottom: auto;
          left: 1rem;
          right: auto;
        }

        :host([position='bottom-right']) {
          top: auto;
          bottom: 1rem;
          left: auto;
          right: 1rem;
        }
        :host([position='bottom-left']) {
          top: auto;
          bottom: 1rem;
          left: 1rem;
          right: auto;
        }
        :host([position='top-right']) ::slotted(*),
        :host([position='top-left']) ::slotted(*) {
          margin-bottom: 0.5rem;
        }

        :host([position='bottom-right']) ::slotted(*),
        :host([position='bottom-left']) ::slotted(*) {
          margin-top: 0.5rem;
        }

        /* mobile */
        :host([mobile][position='top-right']),
        :host([mobile][position='top-left']) {
          top: 4.5rem;
        }
        :host([mobile]) {
          width: calc(100% - 2rem);
        }
      `,
    ];
  }
  render() {
    return html` <slot></slot> `;
  }
  constructor() {
    super();
    this.position = 'top-right';
  }

  handleResize() {
    this.mobile = windowSize.util.valueMatches(document.documentElement.clientWidth, '<', 640);
  }
}
customElements.define('sh-notifications', SHNotifications);
